import { portalApi } from "./client";
import type {
  CollectionAiFeedbackResult,
  DocumentDownloadDto,
  EducationContentDownloadDto,
  ESignStartResult,
  InHouseConsentDto,
  InHouseSignatureResult,
  PortalDealSummaryDto,
  PortalDocumentDto,
  PortalItemBucket,
  PortalItemDto,
  PortalItemRowDto,
  PortalPackageDto,
  PortalPackageSummaryDto,
  PortalRequestsCount,
  PortalUploadResult,
  ReturnApprovalChainRequest,
  SubmitEducationRequest,
} from "./types";

// External Intake & Collection v1.0 PR 4 — package + item endpoints under
// /api/portal/*. PR 6 adds the pre-submit feedback hook (upload returns an
// advisory; the recipient then calls confirm to finalize) and a form
// preview-feedback endpoint.

export const portalPackagesApi = {
  listMine: () => portalApi.get<PortalPackageSummaryDto[]>("/packages/me"),
  // External Portal v2 §4.4 — flat "all my documents" view.
  listMyDocuments: () => portalApi.get<PortalDocumentDto[]>("/documents"),
  // Post-Rollout PR 4 (PROB-28b) — flat item list sliced by lifecycle
  // bucket; powers the item-level Outstanding (requests) + Documents tabs.
  listMyItems: (bucket: PortalItemBucket) =>
    portalApi.get<PortalItemRowDto[]>("/recipients/me/items", { bucket }),
  // Cheap aggregate COUNT of outstanding request items for the current portal
  // session — same recipient scope / in-flight status set / archived exclusion
  // as listMyItems("requests"), split by who owes the next move. Powers the
  // sidebar Requests badge (WS-3), the Brokerage Home "needs attention" card
  // (WS-2), the buyer/seller Home requests strip, and the Requests tab header;
  // the notifications unread-count precedent.
  //
  // `total` is the value this endpoint has always returned. Read `needsYou`
  // when the surface is telling someone to act — `total` includes items the
  // recipient has already submitted and Phoenix has not yet reviewed.
  requestsCount: () =>
    portalApi.get<PortalRequestsCount>("/recipients/me/items/requests-count"),
  // External Portal v2 PR 2 (commit D) — read-only deals tab. Empty array
  // when the portal user isn't linked to any counterparty.
  listMyDeals: () => portalApi.get<PortalDealSummaryDto[]>("/deals/mine"),
  get: (packageId: string) => portalApi.get<PortalPackageDto>(`/packages/${packageId}`),

  // Short-lived read SAS for the filed document attached to an item the
  // caller owns. Scoped server-side to the portal session's own packages.
  getItemDocument: (itemId: string) =>
    portalApi.get<DocumentDownloadDto>(`/items/${itemId}/document-download-url`),

  // Upload templates (U6b) — the item's "Form to complete", streamed through
  // the scoped endpoint (never a URL). The first download moves a Pending
  // item to In progress server-side; re-read the package afterwards.
  downloadFormAttachment: (itemId: string) =>
    portalApi.getBlob(`/items/${itemId}/form-attachment`),

  saveFormData: (itemId: string, savedFormDataJson: string) =>
    portalApi.put<PortalItemDto>(`/items/${itemId}/form-data`, { savedFormDataJson }),

  // Review loop — `responseNote` is the recipient's reply when the item owes a
  // response to a return (required then; the server refuses a blank one).
  // Always sends a JSON body so an omitted note binds as absent.
  submitForm: (itemId: string, responseNote?: string) =>
    portalApi.post<PortalItemDto>(`/items/${itemId}/submit-form`, { responseNote }),

  // Review loop — send a returned item back unchanged, with the recipient's
  // note. No signing step; only offered while canSendBackWithoutChanges.
  respondWithoutChanges: (itemId: string, note: string) =>
    portalApi.post<PortalItemDto>(`/items/${itemId}/respond-without-changes`, { note }),

  previewFormFeedback: (itemId: string) =>
    portalApi.post<CollectionAiFeedbackResult>(`/items/${itemId}/preview-feedback`),

  upload: (itemId: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return portalApi.upload<PortalUploadResult>(`/items/${itemId}/upload`, form);
  },

  confirmUpload: (stagedSubmissionId: string, responseNote?: string) =>
    portalApi.post<PortalItemDto>(`/staged-submissions/${stagedSubmissionId}/confirm`, {
      responseNote,
    }),

  // PR 8 §9 — hybrid e-signature. ESign items go through DocuSign;
  // RequiresSignature items go through the in-house consent + canvas flow.
  // Review loop — when the item owes a response the note is required HERE,
  // before signing; the server attaches it when DocuSign completes.
  startESign: (itemId: string, responseNote?: string) =>
    portalApi.post<ESignStartResult>(`/items/${itemId}/esign/start`, { responseNote }),

  getInHouseConsent: (itemId: string) =>
    portalApi.get<InHouseConsentDto>(`/items/${itemId}/in-house-consent`),

  acceptInHouseConsent: (itemId: string, consentVersion: string) =>
    portalApi.post<{ itemId: string }>(`/items/${itemId}/in-house-consent`, { consentVersion }),

  submitInHouseSignature: (
    itemId: string,
    payload: {
      consentVersion: string;
      signatureDataUri: string;
      signatureMethod: string;
      responseNote?: string;
    },
  ) => portalApi.post<InHouseSignatureResult>(`/items/${itemId}/in-house-signature`, payload),

  // Sender-Fields v1 — render the item's pre-filled letter to a PDF for the
  // recipient to review before signing. Returns the PDF blob.
  // For an approval-chain item this is the CURRENT composed PDF.
  getLetterPreview: (itemId: string) =>
    portalApi.getBlob(`/items/${itemId}/letter-preview`),

  // Approval chains — the caller returns the document with a required comment
  // (≤ 4,000 characters). The chain stops until staff revise it; a revised
  // version restarts from the first party. Endorse / sign go through the
  // in-house consent + signature endpoints above.
  returnApprovalChain: (itemId: string, comment: string) =>
    portalApi.post<PortalItemDto>(`/items/${itemId}/chain/return`, {
      comment,
    } satisfies ReturnApprovalChainRequest),

  // Series 3 — Education item content + submit.
  getEducationContent: (itemId: string) =>
    portalApi.get<EducationContentDownloadDto>(`/items/${itemId}/education-content`),

  submitEducation: (itemId: string, payload: SubmitEducationRequest) =>
    portalApi.post<PortalItemDto>(`/items/${itemId}/submit-education`, payload),
};
